import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { RoleGuard } from './guards/role.guard';
import { TenantResolveGuard } from './guards/tenant-resolve.guard';

const routes: Routes = [
  {
    path: 'tenant/:tenantId',
    canActivate: [TenantResolveGuard],
    canActivateChild: [TenantResolveGuard],
    children: [
      {
        path: 'home',
        loadChildren: () => import('./home/home.module').then((m) => m.HomePageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'login',
        loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginPageModule),
      },
      {
        path: 'checkin',
        loadChildren: () => import('./pages/checkin/checkin.module').then((m) => m.CheckinPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'kiosk-checkin',
        loadChildren: () => import('./pages/kiosk-checkin/kiosk-checkin.module').then((m) => m.KioskCheckinPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'line-confirmation',
        loadChildren: () => import('./pages/line-confirmation/line-confirmation.module').then((m) => m.LineConfirmationPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'refractions1',
        loadChildren: () => import('./pages/refractions1/refractions1.module').then((m) => m.Refractor1PageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'refractions2',
        loadChildren: () => import('./pages/refractions2/refractions2.module').then((m) => m.Refractor2PageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'consultation',
        loadChildren: () => import('./pages/consultation-list/consultation-list.module').then((m) => m.ConsultationListPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['OPHTHALMOLOGIST'] },
      },
      {
        path: 'consultation/:visitId',
        loadChildren: () => import('./pages/consultation/consultation.module').then((m) => m.ConsultationPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['OPHTHALMOLOGIST'] },
      },
      {
        path: 'diagnostics',
        loadChildren: () => import('./pages/diagnostics/diagnostics.module').then((m) => m.DiagnosticsPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['DIAGNOSTICS_TECH'] },
      },
      {
        path: 'billing',
        loadChildren: () => import('./pages/billing/billing.module').then((m) => m.BillingPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'or-schedule',
        loadChildren: () => import('./pages/or-schedule/or-schedule.module').then((m) => m.OrSchedulePageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'or-live-board',
        loadChildren: () => import('./pages/or-live-board/or-live-board.module').then((m) => m.OrLiveBoardPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'or-procedure',
        loadChildren: () => import('./pages/or-procedure/or-procedure.module').then((m) => m.OrProcedurePageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'philhealth',
        loadChildren: () => import('./pages/philhealth/philhealth.module').then((m) => m.PhilhealthPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'frontdesk',
        loadChildren: () => import('./pages/frontdesk-queue/frontdesk-queue.module').then((m) => m.FrontdeskQueuePageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['FRONTDESK'] },
      },
      {
        path: 'appointment-confirmation',
        loadChildren: () => import('./pages/appointment-confirmation/appointment-confirmation.module').then((m) => m.AppointmentConfirmationPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'patient-search',
        loadChildren: () => import('./pages/patient-search/patient-search.module').then((m) => m.PatientSearchPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'patient-profile',
        loadChildren: () => import('./pages/patient-profile/patient-profile.module').then((m) => m.PatientProfilePageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'visit-wizard',
        loadChildren: () => import('./pages/visit-wizard/visit-wizard.module').then((m) => m.VisitWizardPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'insurance-approval',
        loadChildren: () => import('./pages/insurance-approval/insurance-approval.module').then((m) => m.InsuranceApprovalPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'triage-handoff',
        loadChildren: () => import('./pages/triage-handoff/triage-handoff.module').then((m) => m.TriageHandoffPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'qr-scan',
        loadChildren: () => import('./qr-scan/qr-scan.module').then((m) => m.QrScanPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'admin',
        loadChildren: () => import('./pages/admin-users/admin-users.module').then((m) => m.AdminUsersPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['ADMIN'] },
      },
      {
        path: 'first-login',
        loadChildren: () => import('./pages/first-login/first-login.module').then((m) => m.FirstLoginPageModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'oauth-callback',
        loadChildren: () => import('./pages/oauth-callback/oauth-callback.module').then((m) => m.OAuthCallbackPageModule),
      },
      {
        path: 'patient-qrcode',
        loadChildren: () => import('./pages/patient-qrcode/patient-qrcode.module').then((m) => m.PatientQrcodePageModule),
      },
      {
        path: 'patient-intake',
        loadChildren: () => import('./pages/patient-intake/patient-intake.module').then((m) => m.PatientIntakePageModule),
      },
      {
        path: 'patient-queue',
        loadChildren: () => import('./pages/patient-queue/patient-queue.module').then((m) => m.PatientQueuePageModule),
      },
      {
        path: 'patient-routes',
        loadChildren: () => import('./pages/patient-routes/patient-routes.module').then((m) => m.PatientRoutesPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['NURSE'] },
      },
      {
        path: 'refraction',
        loadChildren: () => import('./pages/refraction/refraction.module').then((m) => m.RefractionPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['OPTOMETRIST'] },
      },
      {
        path: 'operation',
        loadChildren: () => import('./pages/operation/operation.module').then((m) => m.OperationPageModule),
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ['ADMIN'] },
      },
      {
        path: '',
        redirectTo: 'login',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '',
    loadChildren: () => import('./pages/tenant-redirect/tenant-redirect.module').then((m) => m.TenantRedirectPageModule),
  },
  {
    path: '**',
    loadChildren: () => import('./pages/tenant-redirect/tenant-redirect.module').then((m) => m.TenantRedirectPageModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
